import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useToast } from "../ui/ToastContainer";
import { enrollmentService } from "../../services/enrollmentService";
import { parseApiError } from "../../utils/errorHandler";
import { CalendarClock } from "lucide-react";

const PRESETS = [
  { key: "3m", label: "3 months", days: 90 },
  { key: "6m", label: "6 months", days: 180 },
  { key: "9m", label: "9 months", days: 270 },
  { key: "1y", label: "1 year", days: 365 },
  { key: "2y", label: "2 years", days: 730 },
  { key: "3y", label: "3 years", days: 1095 },
] as const;

type PresetKey = (typeof PRESETS)[number]["key"];

const INACTIVE_STATUSES = new Set(["cancelled", "expired", "completed"]);

interface ExtendCoursesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  student: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  enrollments: any[]; // already filtered to this student's enrollments by parent
}

export function ExtendCoursesDialog({
  isOpen,
  onClose,
  onSuccess,
  student,
  enrollments,
}: ExtendCoursesDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [preset, setPreset] = useState<PresetKey | null>(null);
  const [customDate, setCustomDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setPreset(null);
      setCustomDate("");
      setError(null);
    }
  }, [isOpen]);

  const activeEnrollments = enrollments.filter(
    (e) => !INACTIVE_STATUSES.has(e.status)
  );
  const inactiveEnrollments = enrollments.filter((e) =>
    INACTIVE_STATUSES.has(e.status)
  );
  const allActiveSelected =
    activeEnrollments.length > 0 &&
    activeEnrollments.every((e) => selectedIds.has(e._id));

  const handleToggleOne = (id: string, isInactive: boolean) => {
    if (isInactive) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleToggleAll = () => {
    if (allActiveSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeEnrollments.map((e) => e._id)));
    }
  };

  const canSubmit =
    selectedIds.size > 0 && (preset !== null || customDate !== "");

  const handleExtend = async () => {
    if (!canSubmit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await enrollmentService.extendEnrollments(
        [...selectedIds],
        preset ?? undefined,
        customDate || undefined
      );
      const extended = response?.extended ?? [];
      showSuccess(
        `Extended ${extended.length} enrollment${extended.length > 1 ? "s" : ""}`,
        "Success"
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Extend enrollments failed:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Extension Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const studentDisplayName = student
    ? `${student.firstName ?? ""} ${student.lastName ?? ""}`.trim() ||
      student.email ||
      ""
    : "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Extend Courses"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        {/* Student header */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student
          </label>
          <div className="text-sm text-gray-900 font-medium">
            {studentDisplayName}
          </div>
        </div>

        {/* Choose courses */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Choose courses
            </label>
            {activeEnrollments.length > 0 && (
              <button
                type="button"
                onClick={handleToggleAll}
                className="text-xs text-indigo-600 hover:underline"
              >
                {allActiveSelected ? "Clear all" : "Select all"}
              </button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
            {enrollments.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No enrollments to extend.
              </div>
            ) : (
              enrollments.map((enrollment) => {
                const isInactive = INACTIVE_STATUSES.has(enrollment.status);
                const isSelected = selectedIds.has(enrollment._id);
                const currentExpiry = enrollment.enrollmentExpiresAt
                  ? new Date(enrollment.enrollmentExpiresAt).toLocaleDateString()
                  : "No expiry";
                return (
                  <label
                    key={enrollment._id}
                    className={`flex items-center gap-3 px-3 py-2 ${
                      isInactive
                        ? "bg-gray-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isInactive}
                      onChange={() =>
                        handleToggleOne(enrollment._id, isInactive)
                      }
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          isInactive ? "text-gray-400" : "text-gray-900"
                        } truncate`}
                      >
                        {enrollment.course?.title ?? enrollment.course}
                      </p>
                      <p className="text-xs text-gray-500">
                        Current expiry: {currentExpiry}
                        {isInactive && (
                          <span className="ml-2 text-amber-600">
                            (inactive — {enrollment.status})
                          </span>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })
            )}
          </div>
          {inactiveEnrollments.length > 0 && (
            <p className="text-xs text-gray-500">
              Cancelled, expired, and completed enrollments can't be extended.
            </p>
          )}
        </div>

        {/* Choose new expiry */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Choose new expiry
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  setPreset(p.key);
                  setCustomDate("");
                }}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  preset === p.key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-indigo-400"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500">or</div>
          <Input
            type="date"
            value={customDate}
            onChange={(e) => {
              setCustomDate(e.target.value);
              setPreset(null);
            }}
            disabled={preset !== null}
            placeholder="Custom date"
          />
        </div>

        {/* Live preview */}
        <div className="p-3 text-sm bg-gray-50 rounded-md border border-gray-100">
          {selectedIds.size === 0 ? (
            <span className="text-gray-500">Select at least one course.</span>
          ) : !preset && !customDate ? (
            <span className="text-gray-500">
              Choose a preset or a custom date.
            </span>
          ) : (
            <span className="text-gray-700">
              Extending {selectedIds.size} enrollment
              {selectedIds.size > 1 ? "s" : ""} by{" "}
              {preset
                ? PRESETS.find((p) => p.key === preset)?.label
                : "custom duration"}
              . New expiry will be max(today, current expiry) + duration.
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExtend}
            disabled={isLoading || !canSubmit}
            className="gap-2"
          >
            <CalendarClock size={16} />
            {isLoading ? "Extending..." : "Extend"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}